import React, { useState, useEffect } from 'react';
import { 
  auth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  updateProfile,
  sendPasswordResetEmail,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider
} from '../firebase';

export default function AuthModal({ tab, onClose }) {
  const [activeTab, setActiveTab] = useState(tab || 'login'); // 'login', 'signup', 'verification-pending'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getFriendlyErrorMessage = (error) => {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Try logging in instead.';
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Incorrect email or password. Please try again.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/too-many-requests':
        return 'Too many login attempts. Please try again later.';
      default:
        return error.message || 'An error occurred during authentication.';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (activeTab === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;
        if (user.emailVerified || user.email === 'admin@mrexcelsrilanka.com') {
          setSuccess('Welcome back! Login successful.');
          setTimeout(() => onClose(), 1500);
        } else {
          // Send verification link if they aren't verified yet
          await sendEmailVerification(user);
          setVerificationEmail(user.email);
          setError('');
          setSuccess('A verification link has been sent to your email.');
          setActiveTab('verification-pending');
        }
      } else {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match.');
          setLoading(false);
          return;
        }
        if (!formData.agreeTerms) {
          setError('You must agree to the Terms & Conditions.');
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;
        await updateProfile(user, { displayName: formData.name });
        
        if (user.email === 'admin@mrexcelsrilanka.com') {
          setSuccess('Admin account created successfully!');
          setTimeout(() => onClose(), 1500);
        } else {
          await sendEmailVerification(user);
          setVerificationEmail(user.email);
          setSuccess('Account created! A verification link has been sent to your email.');
          setActiveTab('verification-pending');
        }
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address in the email field first.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      setSuccess('Password reset link sent to your email.');
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const checkEmailVerification = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          setSuccess('Email verified successfully! Welcome.');
          setTimeout(() => {
            onClose();
          }, 1500);
        } else {
          setError('Email is not verified yet. Please check your inbox and click the verification link.');
        }
      } else {
        setError('No active session found. Please log in again.');
        setActiveTab('login');
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (resendTimer > 0) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setSuccess('Verification email resent! Please check your inbox.');
        setResendTimer(60);
      } else {
        setError('No active session found.');
        setActiveTab('login');
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = async () => {
    setError('');
    setSuccess('');
    try {
      await signOut(auth);
      setActiveTab('login');
    } catch (err) {
      console.error(err);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      setSuccess(`Signed in as ${result.user.displayName || result.user.email}`);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const provider = new OAuthProvider('microsoft.com');
      const result = await signInWithPopup(auth, provider);
      setSuccess(`Signed in as ${result.user.displayName || result.user.email}`);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal open" onClick={onClose} style={{ zIndex: 2000 }}>
      <div 
        className="modal-dialog auth-dialog" 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(100%, 460px)',
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(11, 143, 85, 0.15)',
          borderRadius: '24px',
          padding: '36px 32px',
          boxShadow: '0 24px 60px rgba(14, 33, 24, 0.15)',
          backdropFilter: 'blur(10px)',
          position: 'relative'
        }}
      >
        <button 
          className="modal-close" 
          type="button" 
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '20px',
            top: '20px',
            border: 'none',
            background: 'none',
            fontSize: '1.5rem',
            color: '#607067',
            cursor: 'pointer',
            lineHeight: 1
          }}
        >
          &times;
        </button>

        {activeTab === 'verification-pending' ? (
          <div className="verification-container" style={{ textAlign: 'center', marginTop: '10px' }}>
            <div className="mail-icon-wrapper" style={{
              width: '80px',
              height: '80px',
              background: '#e9f6ef',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 20px rgba(11, 143, 85, 0.12)',
              animation: 'pulse 2s infinite'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#0b8f55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            
            <h2 style={{ fontSize: '1.4rem', fontWeight: 850, margin: '0 0 10px 0', color: '#0e2118' }}>
              Verify Your Email
            </h2>
            
            <p style={{ fontSize: '0.86rem', color: '#607067', lineHeight: '1.5', marginBottom: '24px' }}>
              We've sent a secure verification link to <strong style={{ color: '#0b8f55' }}>{verificationEmail}</strong>. Please check your inbox and click the link to verify your email.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                type="button"
                onClick={checkEmailVerification}
                disabled={loading}
                style={{
                  background: '#0b8f55',
                  color: '#fff',
                  padding: '12px',
                  borderRadius: '999px',
                  fontWeight: 800,
                  fontSize: '0.94rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 24px rgba(11,143,85,0.22)',
                  border: 0,
                  width: '100%',
                  minHeight: '48px'
                }}
              >
                {loading ? (
                  <span className="spinner" style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></span>
                ) : (
                  "I've Verified My Email"
                )}
              </button>

              <button
                type="button"
                onClick={resendVerification}
                disabled={loading || resendTimer > 0}
                style={{
                  background: 'transparent',
                  border: '1.5px solid #0b8f55',
                  color: '#0b8f55',
                  padding: '12px',
                  borderRadius: '999px',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  cursor: (loading || resendTimer > 0) ? 'not-allowed' : 'pointer',
                  width: '100%',
                  minHeight: '48px',
                  transition: 'all 0.2s ease',
                  opacity: (loading || resendTimer > 0) ? 0.6 : 1
                }}
              >
                {resendTimer > 0 ? `Resend Link (${resendTimer}s)` : 'Resend Verification Link'}
              </button>

              <button
                type="button"
                onClick={handleCancelVerification}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#607067',
                  padding: '8px',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  marginTop: '6px'
                }}
              >
                Cancel & Back to Log In
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="auth-header" style={{ textAlign: 'center', marginBottom: '28px' }}>
              <img 
                src="/assets/images/excel-logo-modern.svg" 
                alt="Excel Logo" 
                style={{ width: '48px', height: '48px', margin: '0 auto 12px' }}
              />
              <h2 style={{ fontSize: '1.6rem', fontWeight: 850, margin: 0, color: '#0e2118' }}>
                {activeTab === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p style={{ fontSize: '0.84rem', color: '#607067', marginTop: '6px' }}>
                {activeTab === 'login' 
                  ? 'Access your Excel skill assessments & resources' 
                  : 'Join the Mr Excel learning community today'}
              </p>
            </div>

            {/* Tab Selector */}
            <div 
              className="auth-tabs" 
              style={{
                display: 'flex',
                background: '#eef3ef',
                padding: '4px',
                borderRadius: '12px',
                marginBottom: '24px'
              }}
            >
              <button
                type="button"
                onClick={() => { setActiveTab('login'); setError(''); setSuccess(''); }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  border: 0,
                  borderRadius: '9px',
                  fontWeight: 800,
                  fontSize: '0.86rem',
                  cursor: 'pointer',
                  background: activeTab === 'login' ? '#fff' : 'transparent',
                  color: activeTab === 'login' ? '#0b8f55' : '#607067',
                  boxShadow: activeTab === 'login' ? '0 4px 10px rgba(0,0,0,0.05)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                Log In
              </button>
              <button
                type="button"
                onClick={() => { setActiveTab('signup'); setError(''); setSuccess(''); }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  border: 0,
                  borderRadius: '9px',
                  fontWeight: 800,
                  fontSize: '0.86rem',
                  cursor: 'pointer',
                  background: activeTab === 'signup' ? '#fff' : 'transparent',
                  color: activeTab === 'signup' ? '#0b8f55' : '#607067',
                  boxShadow: activeTab === 'signup' ? '0 4px 10px rgba(0,0,0,0.05)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                Sign Up
              </button>
            </div>

            {error && (
              <div 
                style={{
                  background: '#fef1f1',
                  color: '#d92d20',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  marginBottom: '18px',
                  border: '1px solid #fda29b'
                }}
              >
                {error}
              </div>
            )}

            {success && (
              <div 
                style={{
                  background: '#ecfdf3',
                  color: '#039855',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  marginBottom: '18px',
                  border: '1px solid #a9efc5'
                }}
              >
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {activeTab === 'signup' && (
                <div className="form-group" style={{ display: 'grid', gap: '6px' }}>
                  <label htmlFor="auth-name" style={{ fontSize: '0.78rem', fontWeight: 800, color: '#35463d' }}>Full Name</label>
                  <input
                    id="auth-name"
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    style={{
                      padding: '12px',
                      border: '1px solid #cfdad1',
                      borderRadius: '10px',
                      background: '#fff',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              )}

              <div className="form-group" style={{ display: 'grid', gap: '6px' }}>
                <label htmlFor="auth-email" style={{ fontSize: '0.78rem', fontWeight: 800, color: '#35463d' }}>Email Address</label>
                <input
                  id="auth-email"
                  name="email"
                  type="email"
                  placeholder="e.g. name@company.com"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    padding: '12px',
                    border: '1px solid #cfdad1',
                    borderRadius: '10px',
                    background: '#fff',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div className="form-group" style={{ display: 'grid', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label htmlFor="auth-password" style={{ fontSize: '0.78rem', fontWeight: 800, color: '#35463d' }}>Password</label>
                  {activeTab === 'login' && (
                    <a 
                      href="#/forgot-password" 
                      onClick={handleForgotPassword}
                      style={{ fontSize: '0.74rem', color: '#0b8f55', fontWeight: 700, textDecoration: 'none' }}
                    >
                      Forgot Password?
                    </a>
                  )}
                </div>
                <input
                  id="auth-password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  style={{
                    padding: '12px',
                    border: '1px solid #cfdad1',
                    borderRadius: '10px',
                    background: '#fff',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              {activeTab === 'signup' && (
                <div className="form-group" style={{ display: 'grid', gap: '6px' }}>
                  <label htmlFor="auth-confirm" style={{ fontSize: '0.78rem', fontWeight: 800, color: '#35463d' }}>Confirm Password</label>
                  <input
                    id="auth-confirm"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    style={{
                      padding: '12px',
                      border: '1px solid #cfdad1',
                      borderRadius: '10px',
                      background: '#fff',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              )}

              {activeTab === 'signup' && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginTop: '4px' }}>
                  <input
                    name="agreeTerms"
                    type="checkbox"
                    checked={formData.agreeTerms}
                    onChange={handleInputChange}
                    style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#607067', lineHeight: '1.4' }}>
                    I agree to the <a href="#/terms" style={{ color: '#0b8f55', fontWeight: 700 }}>Terms of Service</a> and <a href="#/privacy" style={{ color: '#0b8f55', fontWeight: 700 }}>Privacy Policy</a>.
                  </span>
                </label>
              )}

              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading}
                style={{
                  marginTop: '8px',
                  background: '#0b8f55',
                  borderColor: '#0b8f55',
                  color: '#fff',
                  padding: '12px',
                  borderRadius: '999px',
                  fontWeight: 800,
                  fontSize: '0.94rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 24px rgba(11,143,85,0.22)',
                  opacity: loading ? 0.8 : 1,
                  pointerEvents: loading ? 'none' : 'auto',
                  border: 0,
                  width: '100%',
                  minHeight: '48px'
                }}
              >
                {loading ? (
                  <span className="spinner" style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></span>
                ) : (
                  activeTab === 'login' ? 'Log In to My Account' : 'Create My Account'
                )}
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', color: '#bcc9c0' }}>
              <div style={{ flex: 1, height: '1px', background: '#dce9e1' }}></div>
              <span style={{ padding: '0 12px', fontSize: '0.74rem', color: '#607067', fontWeight: 700 }}>OR CONTINUE WITH</span>
              <div style={{ flex: 1, height: '1px', background: '#dce9e1' }}></div>
            </div>

            {/* Social Logins */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px',
                  background: '#fff',
                  border: '1px solid #cfdad1',
                  borderRadius: '10px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#35463d',
                  cursor: 'pointer'
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.92h6.69c-.29 1.5-1.14 2.77-2.4 3.61v3h3.84c2.24-2.07 3.53-5.11 3.53-8.46Z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.84-3c-1.07.72-2.45 1.16-4.09 1.16-3.15 0-5.81-2.13-6.76-5.01H1.32v3.1A12 12 0 0 0 12 24Z"/>
                  <path fill="#FBBC05" d="M5.24 14.24a7.2 7.2 0 0 1 0-2.48V8.66H1.32a12 12 0 0 0 0 6.68l3.92-3.1Z"/>
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A12 12 0 0 0 1.32 8.66l3.92 3.1c.95-2.88 3.61-5.01 6.76-5.01Z"/>
                </svg>
                Google
              </button>
              <button
                type="button"
                onClick={handleMicrosoftSignIn}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px',
                  background: '#fff',
                  border: '1px solid #cfdad1',
                  borderRadius: '10px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#35463d',
                  cursor: 'pointer'
                }}
              >
                <svg viewBox="0 0 23 23" width="14" height="14">
                  <path fill="#f35325" d="M0 0h11v11H0z"/>
                  <path fill="#81bc06" d="M12 0h11v11H12z"/>
                  <path fill="#05a6f0" d="M0 12h11v11H0z"/>
                  <path fill="#ffba08" d="M12 12h11v11H12z"/>
                </svg>
                Microsoft
              </button>
            </div>
          </>
        )}

        {/* CSS Keyframe Animation */}
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(11, 143, 85, 0.4);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(11, 143, 85, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(11, 143, 85, 0);
            }
          }
        `}} />
      </div>
    </div>
  );
}
